import { useEffect, useState } from 'react';

// The y-coordinate treated as "the top of the readable viewport": the fixed
// header height (`h-14` = 56px in header.tsx) plus the 32px gap that makes a
// section feel active slightly before its heading touches the header.
//
// This must stay consistent with the `scroll-mt-20` on the anchor targets
// (award-card, certification-card, education-card, experience-card,
// project-card). Change the header height or that utility without changing
// this, and the spy highlights the wrong section — silently, since nothing
// asserts the relationship.
const HEADER_OFFSET = 56 + 32;

/**
 * Smooth-scroll a section into view below the fixed header. The header
 * clearance comes from `scroll-mt-20` on the anchor targets themselves, so
 * native anchor navigation and back/forward restore land correctly too.
 */
export const scrollToSection = (id: string) => {
  document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
};

/**
 * Scroll-spy hook: section element IDs in, the active section ID out.
 *
 * - The scroll listener is passive and rAF-throttled.
 * - State updates are equality-guarded: the state is a plain string, so
 *   React bails out of re-renders when the active section is unchanged.
 * - The effect depends on the *content* of the ID list (joined string),
 *   not the array identity, so callers may build the list inline without
 *   re-attaching the listener on every render.
 */
export function useScrollSpy(sectionIds: string[]): string {
  const [activeId, setActiveId] = useState('');

  // Section IDs are slugs, so '|' is a safe delimiter.
  const idsKey = sectionIds.join('|');

  useEffect(() => {
    const ids = idsKey ? idsKey.split('|') : [];
    if (ids.length === 0) return;

    const computeActiveId = (): string => {
      // A single section is always active.
      if (ids.length === 1) return ids[0];

      // At the very top of the page, highlight the first section; at the
      // very bottom, the last one (10px tolerance).
      if (window.scrollY <= 10) return ids[0];
      if (
        window.innerHeight + window.scrollY >=
        document.documentElement.scrollHeight - 10
      ) {
        return ids[ids.length - 1];
      }

      // A section is active when it spans the header offset line.
      for (const id of ids) {
        const element = document.getElementById(id);
        if (!element) continue;
        const rect = element.getBoundingClientRect();
        if (rect.top <= HEADER_OFFSET && rect.bottom > HEADER_OFFSET) {
          return id;
        }
      }

      // Nothing straddles the line (gaps between cards, or a section shorter
      // than the gap): fall back to the nearest section, but only one at or
      // above the line plus 100px of slack. Without that bound, a section still
      // well below the fold would win while the user is reading the one above
      // it; the slack lets a section just about to scroll in take over.
      let closestId = '';
      let closestDistance = Infinity;
      for (const id of ids) {
        const element = document.getElementById(id);
        if (!element) continue;
        const distance = Math.abs(
          element.getBoundingClientRect().top - HEADER_OFFSET,
        );
        if (
          distance < closestDistance &&
          element.getBoundingClientRect().top <= HEADER_OFFSET + 100
        ) {
          closestDistance = distance;
          closestId = id;
        }
      }
      return closestId;
    };

    let ticking = false;

    const handleScroll = () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        // String state: setState with an unchanged value bails out, so a
        // stream of scroll events does not cause a stream of re-renders.
        setActiveId(computeActiveId());
        ticking = false;
      });
    };

    // Initial check, then track scrolling.
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [idsKey]);

  // Derive instead of resetting state in the effect: with no sections there
  // is no active ID, and after the ID list changes the rAF-scheduled initial
  // check refreshes the state.
  return sectionIds.length === 0 ? '' : activeId;
}
